import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { CloseIcon } from '../../../../theme/overrides/CustomIcons';
import { validateVoucher } from '../../../../services/voucher.services';
import { dispatch } from '../../../../redux/store';
import { FormProvider, RHFTextField } from '../../../../components/hook-form';
import Iconify from '../../../../components/Iconify';
import VoucherGivingBasketModal from './VoucherGivingBasketModal';
import {
  setIsGiftCardOpenEvent,
  setIsGivingBasketOpenEvent,
  setIsOpenEvent,
  setIsValidEvent,
  setVoucherCodeEvent,
} from '../../../../redux/slices/voucher';
import VoucherGiftCardModal from './VoucherGiftCardModal';

export default function ValidateVoucherModal() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const processedSearchRef = useRef('');
  const { enqueueSnackbar } = useSnackbar();
  const { isOpen, isValid } = useSelector((state) => state.voucher);

  const resolver = yupResolver(
    Yup.object().shape({
      voucherCode: Yup.string().required('Voucher code is required'),
    })
  );
  const methods = useForm({ resolver });
  const { reset, handleSubmit, setError, setValue } = methods;

  const onClose = () => {
    reset();
    dispatch(setIsOpenEvent(false));
  };

  const validateVoucherCode = useCallback(
    async (voucherCode) => {
      setIsLoading(true);
      try {
        const { data: response } = await validateVoucher(voucherCode);
        const { claimed, valid } = response;

        if (claimed) {
          setError('voucherCode', {});
          enqueueSnackbar('This voucher was already claimed — please try another one.', { variant: 'error' });
        } else if (!valid) {
          setError('voucherCode', {});
          enqueueSnackbar('Sorry, we couldn’t find a voucher with that code.', { variant: 'error' });
        } else {
          dispatch(setIsValidEvent(true));
          enqueueSnackbar('Thank you! Your voucher has been accepted.');
        }
        reset();
        dispatch(setVoucherCodeEvent(voucherCode));
      } catch (e) {
        setError('voucherCode', {});
        enqueueSnackbar('Sorry, we couldn’t find a voucher with that code.', { variant: 'error' });
      }
      setIsLoading(false);
    },
    [enqueueSnackbar, reset, setError]
  );

  /* Read landing query params from the actual location so QR links work on first load. */
  useEffect(() => {
    const currentSearch = location.search || window.location.search || '';
    if (!currentSearch || processedSearchRef.current === currentSearch) return;

    processedSearchRef.current = currentSearch;

    const params = new URLSearchParams(currentSearch);
    const getParamCaseInsensitive = (paramName) => {
      const target = String(paramName).toLowerCase();
      const matchingEntry = Array.from(params.entries()).find(
        ([key]) => String(key).toLowerCase() === target
      );
      return matchingEntry ? matchingEntry[1] : null;
    };

    const voucherCodeParam = getParamCaseInsensitive('voucherCode');
    const voucherParam = getParamCaseInsensitive('voucherDonation');

    /* Treat "true", "1", or even just "?voucherDonation" (no value) as valid */
    const voucher =
      voucherParam === '' ||
      voucherParam?.toLowerCase() === 'true' ||
      voucherParam === '1';

    const donationParamAsCode =
      voucherParam && !voucher ? String(voucherParam).trim() : '';

    const code =
      (voucherCodeParam ? String(voucherCodeParam).trim() : '') ||
      donationParamAsCode;

    if (code) {
      dispatch(setIsOpenEvent(true));
      setValue('voucherCode', code);
      validateVoucherCode(code);
    } else if (voucher) {
      dispatch(setIsOpenEvent(true));
    }

    if (voucher || voucherCodeParam || donationParamAsCode) {
      const newParams = new URLSearchParams(params);
      const keysToDelete = Array.from(newParams.keys()).filter((key) => {
        const lowered = String(key).toLowerCase();
        return lowered === 'voucherdonation' || lowered === 'vouchercode';
      });
      keysToDelete.forEach((key) => newParams.delete(key));
      setSearchParams(newParams);
    }
  }, [location.search, setSearchParams, setValue, validateVoucherCode]);

  /* NEW: Read localStorage "voucherDonationType" and auto-open the correct modal.
     - "giftcard"     -> open Gift Card
     - "givingbasket" -> open Giving Basket
     - any other non-null value -> clear it and do nothing
     - null/absent -> do nothing (stay as is)
     Always clears the key if it exists. */
  useEffect(() => {
    try {
      const raw = localStorage.getItem('voucherDonationType');
      if (raw != null) {
        const value = String(raw).toLowerCase().trim();

        if (value === 'giftcard') {
          dispatch(setIsGiftCardOpenEvent(true));
          dispatch(setIsOpenEvent(false));
        } else if (value === 'givingbasket') {
          dispatch(setIsGivingBasketOpenEvent(true));
          dispatch(setIsOpenEvent(false));
        }
        /* Always clear if not empty */
        localStorage.removeItem('voucherDonationType');
      }
    } catch {
      /* no-op */
    }
  }, []);

  const onValidate = async (data) => {
    await validateVoucherCode(data.voucherCode);
  };

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={isOpen}>
        <DialogTitle>
          <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
            <Box width={{ md: '90%', xs: '80%' }}>
              <Typography variant="h4" mb={2}>
                {isValid ? 'How would you like to donate?' : 'Thanks for scanning! Just enter your voucher code below.'}
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => {
                onClose();
              }}
              sx={{
                position: 'absolute',
                right: 20,
                top: 22,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ overflowX: 'hidden' }}>
          {!isValid && (
            <FormProvider methods={methods} onSubmit={handleSubmit(onValidate)}>
              <RHFTextField name="voucherCode" fullWidth label="Voucher Code" sx={{ mt: 3 }} />
              <Grid container justifyContent="flex-end" mt={3} spacing={2}>
                <Grid item>
                  <Button onClick={onClose} disabled={isLoading} size="large">
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <LoadingButton loading={isLoading} type="submit" size="large" variant="contained">
                    Validate
                  </LoadingButton>
                </Grid>
              </Grid>
            </FormProvider>
          )}
          {isValid && (
            <Box py={5}>
              <Grid container spacing={3}>
                <Grid item flex={1}>
                  <Stack
                    onClick={() => {
                      dispatch(setIsGivingBasketOpenEvent(true));
                      onClose();
                    }}
                    alignItems="center"
                    spacing={3}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: '#f8f8f8',
                      p: 2,
                      borderRadius: 1,
                      transition: '200ms',
                      ':hover': {
                        bgcolor: '#00AB5520',
                      },
                    }}
                  >
                    <Iconify icon="solar:cart-large-bold-duotone" fontSize={50} color="#00AB55" />
                    <Typography variant="h5">Giving Basket</Typography>
                  </Stack>
                </Grid>
                <Grid item flex={1}>
                  <Stack
                    onClick={() => {
                      dispatch(setIsGiftCardOpenEvent(true));
                      onClose();
                    }}
                    alignItems="center"
                    spacing={3}
                    sx={{
                      cursor: 'pointer',
                      p: 2,
                      borderRadius: 1,
                      transition: '200ms',
                      bgcolor: '#f8f8f8',
                      ':hover': {
                        bgcolor: '#00AB5520',
                      },
                    }}
                  >
                    <img src="/icons/love-letter.svg" alt="gc" width={50} />
                    <Typography variant="h5">Gift Card</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <VoucherGivingBasketModal />
      <VoucherGiftCardModal />
    </>
  );
}
